import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { formatAssistantAppointmentTime } from './AssistantAppointmentCard';
import {
  hasUnsettledAssistantBooking,
  readAssistantOperations,
  writeAssistantOperation,
} from './operationRecords';
import { assistantMemberPath } from './paths';
import { newAssistantIdempotencyKey } from './useAssistantCommand';

import type {
  AssistantBooking,
  AssistantBookSlot,
  AssistantCoach,
} from './api';

const BOOKING_NOUNS: Record<AssistantBooking, string> = {
  chemistry: 'Chemistry call',
  series: 'Biweekly sessions',
  single: 'Single session',
};

export type AssistantBookProblem = 'in_progress' | 'storage_unavailable';

/**
 * Records one booking as `submitting` and goes back to the member page, which
 * sends it (see operationDriver). A booking has no preimage to re-check:
 * Moments re-runs the member's context and refuses a start it no longer offers.
 */
export const useAssistantBook = ({
  booking,
  coach,
  memberId,
  viewerTimezone,
}: {
  booking: AssistantBooking;
  coach: AssistantCoach;
  memberId: string;
  viewerTimezone: string;
}) => {
  const navigate = useNavigate();
  const [problem, setProblem] = useState<AssistantBookProblem | null>(null);

  const submit = useCallback(
    (slot: AssistantBookSlot) => {
      setProblem(null);
      // Writing would replace the unsettled record, and with it the only way
      // to recover that booking's lost response.
      if (
        hasUnsettledAssistantBooking(
          readAssistantOperations(memberId),
          coach.personId,
        )
      ) {
        setProblem('in_progress');
        return;
      }
      const { date, time } = formatAssistantAppointmentTime(slot);
      const idempotencyKey = newAssistantIdempotencyKey();
      const recorded = writeAssistantOperation({
        action: 'book',
        booking,
        coachFirstName: coach.firstName,
        coachPersonId: coach.personId,
        idempotencyKey,
        memberId,
        operationId: null,
        outcome: null,
        // Fixed order, sent and replayed as is: see useAssistantCommand.
        requestBytes: JSON.stringify({
          booking,
          coach_person_id: coach.personId,
          idempotency_key: idempotencyKey,
          start_at: slot.startAt,
          viewer_timezone: viewerTimezone,
        }),
        retryAfterSeconds: null,
        startAt: slot.startAt,
        state: 'submitting',
        summary: `${BOOKING_NOUNS[booking]} with ${coach.firstName} ${booking === 'series' ? 'starting' : 'on'} ${date}, ${time}`,
      });
      if (!recorded) {
        setProblem('storage_unavailable');
        return;
      }
      navigate(assistantMemberPath(memberId), { replace: true });
    },
    [booking, coach, memberId, navigate, viewerTimezone],
  );

  return { problem, submit };
};
